'use client'

import React, { useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { Button } from '@/components/ui/button';
import ProjectDetails from '@/components/distribution-analytics/project-details';
import PackageDetails from '@/components/distribution-analytics/package-details';
import SecondaryPackageDetails from '@/components/distribution-analytics/secondary-package-details';
import { ChooseContent } from '@/components/distribution-analytics/choose-content';
import ResultsSection from '@/components/distribution-analytics/results-section';
import { Form } from '@/components/ui/form';
import { useStepperContext } from '../components/stepper-provider';
import Stepper from '../components/stepper';
import PackageContent from '@/components/distribution-analytics/package-content';
import PalletDistributionContent from '@/components/distribution-analytics/pallet-distribution-content';
import { Input } from '@/components/ui/input';
import { Label } from 'recharts';
import DistributionFooter from '../components/distribution-footer';
import { File, Loader2 } from 'lucide-react';
import { useCreateRequestItemMutation } from '@/redux/slices/stack-and-ship/stack-and-ship-apiSlice';
// const formSchema = z.object({
//     // Project Details
//     project_name: z.string().min(1, 'Project name is required'),
//     brand_name: z.string().min(1, 'Brand name is required'),
//     location_name: z.string().min(1, 'Location is required'),
//     customer: z.string().min(1, 'Customer is required'),
//     application_name: z.string().min(1, 'Application is required'),
//     primary_package_type: z.string().min(1, 'Package type is required'),
//     has_tertiary_package: z.string().min(1, 'Please select yes or no'),

//     // Package Details
//     net_contents: z.string().min(1, 'Net contents must be positive'),
//     net_contents_unit: z.string(),
//     filled_top_load: z.string().min(1, 'Top load must be positive'),
//     filled_top_load_unit: z.string(),
//     primary_package_diameter: z.string().min(1, 'Diameter must be positive'),
//     primary_package_diameter_unit: z.string(),
//     primary_package_height: z.string().min(1, 'Height must be positive'),
//     primary_package_height_unit: z.string(),
//     bottle_design: z.string().optional(),

//     // Secondary Package Details
//     secondary_package_type: z.string().min(1, 'Secondary package type is required'),
//     secondary_topload_filled: z.string().min(1, 'Topload must be positive'),
//     secondary_topload_filled_unit: z.string().optional(),
//     secondary_weight: z.string().min(1, 'Weight must be positive'),
//     secondary_weight_unit: z.string(),
//     secondary_length: z.string().min(1, 'Length must be positive'),
//     secondary_length_unit: z.string(),
//     secondary_width: z.string().min(1, 'Width must be positive'),
//     secondary_width_unit: z.string(),
//     secondary_height: z.string().min(1, 'Height must be positive'),
//     secondary_height_unit: z.string(),

//     // Tertiary Package Details
//     t_bct: z.string().min(1, 'Please enter BCT value'),
//     t_bct_unit: z.string().min(1, 'Please select a unit'),
//     t_material: z.string().min(1, 'Please select a material'),
//     t_length: z.string().min(1, 'Please enter a length'),
//     t_length_unit: z.string().min(1, 'Please select a unit'),
//     t_width: z.string().min(1, 'Please enter a width'),
//     t_width_unit: z.string().min(1, 'Please select a unit'),
//     t_height: z.string().min(1, 'Please enter a height'),
//     t_height_unit: z.string().min(1, 'Please select a unit'),
//     t_weight: z.string().min(1, 'Please enter a weight'),
//     t_weight_unit: z.string().min(1, 'Please select a unit'),

//     // Results
//     test_results: z.array(z.object({
//         test_type: z.string().min(1, 'Test type is required'),
//         result: z.string().min(1, 'Result is required'),
//         date: z.string().min(1, 'Date is required')
//     })).optional(),
//     comments: z.string().optional(),
//     attachments: z.array(z.string()).optional(),
//     pallet_base_type: z.string().min(1, 'Pallet base type is required'),
// });
// type FormValues = z.infer<typeof formSchema>;

export default function NewDistributionProject() {
    const { currentStep, handleNext, handleBack } = useStepperContext();
    const [showResults, setShowResults] = useState(false);
    const resultsRef = useRef<HTMLDivElement>(null);
    const [createRequestItem, { isLoading }] = useCreateRequestItemMutation();
    const { isPending } = useSelector((state: any) => state.stackAndShip)

    const onSubmit = async () => {
        if (currentStep === 'define') {
            const submitButton = document.querySelector('button[type="submit"]');
            submitButton?.click();
        } else {
            handleNext();
        }
    };

    const renderStepContent = () => {
        switch (currentStep) {
            case 'define':
                return <ProjectDetails  />;
            case 'package':
                return <PackageContent  />;
            // case 'choose':
            //     return <ChooseContent  />;
            case 'pallet':
            case 'distribution':
                return (
                    <>
                        {/* <PalletDistributionContent form={form} /> */}
                        {showResults && (
                            <div ref={resultsRef} className="mt-8 space-y-6">
                                <div className="bg-gray-50 p-4 rounded-lg">
                                    <h3 className="text-lg text-center font-semibold mb-4">Results and Analysis</h3>

                                    <div className="space-y-4">
                                        <div className="flex flex-col gap-4">
                                            <div className="bg-white p-4 rounded">
                                                <p className="font-medium mb-3">Predicted Values:</p>
                                                <div className="grid grid-cols-4 gap-8 text-center">
                                                    <div>
                                                        <p className="text-sm text-gray-600">Lean</p>
                                                        <p className="font-medium">4.0</p>
                                                    </div>
                                                    <div>
                                                        <p className="text-sm text-gray-600">Walk</p>
                                                        <p className="font-medium">2.4</p>
                                                    </div>
                                                    <div>
                                                        <p className="text-sm text-gray-600">Compression</p>
                                                        <p className="font-medium">0.7</p>
                                                    </div>
                                                    <div>
                                                        <p className="text-sm text-gray-600">Safety Factor</p>
                                                        <p className="font-medium">0.6</p>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="bg-white p-4 rounded">
                                                <p className="font-medium mb-3">Your Risk:</p>
                                                <div className="flex gap-2">
                                                    <div className="bg-bgRed text-black px-4 py-1 rounded">High</div>
                                                    {/* <div className="bg-bgYellow px-4 py-1 rounded">Medium</div>
                                                    <div className="bg-states-success px-4 py-1 rounded">Low</div> */}
                                                </div>
                                            </div>

                                            <div className="flex justify-end">
                                                <div className="flex items-center gap-2">
                                                    <span>Download : PDF </span>
                                                    <button className="bg-blue-50 p-2 rounded">
                                                        <File className='size-4' />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </>
                );
            default:
                return null;
        }
    };

    return (
      
         <div className="max-h-[80vh] overflow-y-scroll bg-white">
            <main className="pt-6 pb-8 px-4 max-w-7xl mx-auto">
                <div >
                    <div  className="space-y-8 pb-10">
                        {renderStepContent()}
                    </div>
                </div>
                  <DistributionFooter
                    requestItem={{}}

                    layout={false}
                    hasUnsavedChanges={true}
                    isLoading={isPending}
                    noNext={false}
                    onSubmit={onSubmit}
                />
            </main>
        </div>
     
    );
}